import React from 'react';
import Konva from 'konva';
import { Group, Line, Arrow } from 'react-konva';
import EditableText from '../EditableText'; // если у вас лежит в components/EditableText.js

/**
 * Полностью берём код из вашего Canvas.js, 
 * где вы рендерите dimensionLines.map((dim, i) => ...).
 * Плюс пробрасываем коллбеки handleDimensionChange, handleDimensionDrag,
 * а также isShiftPressed и т.д.
 */
function DimensionLineComponent({
  dim,
  index,
  // состояние
  dimensionLines,
  setDimensionLines,

  // коллбеки
  handleDimensionDrag,
  handleDimensionChange,

  // у вас ещё есть updateAllDimensionLines, saveState, isShiftPressed и т.д.
  updateAllDimensionLines,
  saveState,
  isShiftPressed,
  /** Порог привязки из боковой панели, px (как у объектов). */
  snapThreshold = 0,
  /** Масштаб Stage: локальные координаты → экранные. */
  effectiveZoom = 1,
  /** Множитель порога, как в привязке объектов: порог × zoom. */
  zoom = 1,

  setSelectedDimensionId,
  setSelectedCylinderId,
  setSelectedFittingId,
  // новые пропсы для определения фиксированных размеров на фитингах
  fittings,
  textZoom = 1,
}) {
  // В вашем коде вы деструктурируете:
  const { isHorizontal, position, x1, y1, x2, y2, value, rawValue, textPosition = { x: 0, y: 0 }, angle, precision = 2, textFontFamily = 'Arial', textFontStyle = 'italic', textColor = '#ff0000', overrideEnabled = false, overrideText = '', lowerOverrideEnabled = false, lowerOverrideText = '' } = dim;

  // Защита от некорректных данных
  if (!position || position.x == null || position.y == null) {
    console.error('DimensionLineComponent: position is undefined or invalid', dim);
    return null;
  }
  if (x1 == null || y1 == null || x2 == null || y2 == null) {
    console.error('DimensionLineComponent: coordinates are undefined', dim);
    return null;
  }

  // Локальный helper из Canvas.js для определения стороны из индекса точки
  const getSideFromPointIndex = (idx) => {
    switch (idx) {
      case 4:
        return 'top';
      case 5:
        return 'bottom';
      case 6:
        return 'left';
      case 7:
        return 'right';
      default:
        return null;
    }
  };

  // Определяем, можно ли редактировать текущий размер (для фитинга с фикс. диаметром запрещаем редактирование ширины)
  const resolveSide = (el) => {
    if (el?.type) return el.type;
    if (typeof el?.idx === 'number') return getSideFromPointIndex(el.idx);
    return null;
  };

  const el1 = dim.elements?.[0];
  const el2 = dim.elements?.[1];
  const sameObject = el1 && el2 && el1.objectId === el2.objectId && el1.objectType === el2.objectType;

  // Разрешаем редактирование текста всегда, но при заблокированной геометрии
  // переключаемся на overrideText вместо изменения размеров объекта
  let lockGeometry = false;
  if (sameObject && el1.objectType === 'fitting' && Array.isArray(fittings)) {
    const fit = fittings.find((f) => f.id === el1.objectId);
    if (fit && fit.isDiameterFixed) {
      const s1 = resolveSide(el1);
      const s2 = resolveSide(el2);
      const affectsWidth = (s1 === 'left' || s1 === 'right') && (s2 === 'left' || s2 === 'right');
      if (affectsWidth) {
        lockGeometry = true;
      }
    }
  }

  // dragBoundFunc — для 0/90 ограничиваем одной осью, для 45/135 даём свободный drag.
  // Горизонтальные линии притягиваются по высоте к другим горизонтальным,
  // вертикальные — по отступу к другим вертикальным. Порог тот же, что у объектов.
  const dragBoundFunc = (pos) => {
    const angleDeg = typeof dim.angle === 'number' ? dim.angle : (isHorizontal ? 0 : 90);
    if (angleDeg % 90 !== 0) return pos;

    const scale = effectiveZoom || 1;
    const thresholdAbs = (snapThreshold || 0) * (zoom || 1);
    const snapToSiblings = (axis) => {
      const raw = axis === 'y' ? pos.y : pos.x;
      if (!(thresholdAbs > 0)) return raw;
      let snapped = raw;
      let best = thresholdAbs;
      dimensionLines.forEach((other, i) => {
        if (i === index || !other?.position) return;
        const otherAngle = typeof other.angle === 'number' ? other.angle : (other.isHorizontal ? 0 : 90);
        if (otherAngle % 90 !== 0) return;
        if (Boolean(other.isHorizontal) !== Boolean(isHorizontal)) return;
        const otherLocal = axis === 'y' ? other.position.y : other.position.x;
        if (otherLocal == null) return;
        const otherAbs = otherLocal * scale;
        const dist = Math.abs(raw - otherAbs);
        if (dist <= best) {
          best = dist;
          snapped = otherAbs;
        }
      });
      return snapped;
    };

    if (isHorizontal) {
      return { x: position.x, y: snapToSiblings('y') };
    }
    return { x: snapToSiblings('x'), y: position.y };
  };

  // Угол поворота размерной линии/текста
  const angleDeg = typeof angle === 'number' ? angle : (isHorizontal ? 0 : 90);

  const formatDimValue = (val) => {
    if (val == null || isNaN(Number(val))) return '';
    let n = rawValue != null && !isNaN(Number(rawValue)) ? Number(rawValue) : Number(val);
    const whole = Math.round(n);
    if (Math.abs(n - whole) <= 0.05) n = whole;
    const maxPrec = Math.max(0, Math.min(2, precision));
    const fixed = n.toFixed(maxPrec);
    // Убираем лишние нули после точки: 60.00 → 60, 60.10 → 60.1, 60.0 → 60
    return fixed.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  };

  const getDisplayText = () => {
    if (overrideEnabled && typeof overrideText === 'string' && overrideText.length > 0) {
      return overrideText;
    }
    return formatDimValue(value);
  };

  // Длина черты под числом: ширина самой надписи, а если снизу вторая строка — берём более длинную.
  const labelWidthAlong = (mainWidth) => {
    if (!lowerOverrideEnabled) return mainWidth;
    const lowerTextContent = (typeof lowerOverrideText === 'string' && lowerOverrideText.length > 0) ? lowerOverrideText : '';
    if (!lowerTextContent) return mainWidth;
    const fs = (dim.textFontSize || 16) * (textZoom || 1);
    const lower = new Konva.Text({ text: lowerTextContent, fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
    return Math.max(mainWidth, lower.width());
  };


  return (
    <Group
      x={position.x}
      y={position.y}
      draggable
      dragBoundFunc={dragBoundFunc}
      onDragStart={(e) => {
        // Блокируем перетаскивание при использовании средней кнопки мыши (панорамирование)
        if (e.evt && e.evt.button === 1) {
          e.target.stopDrag();
          return;
        }
      }}
      onDragMove={(e) => handleDimensionDrag(e, index)}
      onDblClick={(e) => {
        e.cancelBubble = true;
        if (typeof setSelectedFittingId === 'function') setSelectedFittingId(null);
        if (typeof setSelectedCylinderId === 'function') setSelectedCylinderId(null);
        if (typeof setSelectedDimensionId === 'function') setSelectedDimensionId(index);
      }}
      onClick={(e) => {
        e.cancelBubble = true;
        setSelectedDimensionId(index);
        setSelectedCylinderId(null);
        if (typeof setSelectedFittingId === 'function') setSelectedFittingId(null);
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        setSelectedDimensionId(index);
        setSelectedCylinderId(null);
        if (typeof setSelectedFittingId === 'function') setSelectedFittingId(null);
      }}
    >
      {/* Панель настроек для размерной линии (рендерится в Canvas поверх Stage) */}
      {/** Выносные линии */}
      {angleDeg % 90 === 0 ? (
        isHorizontal ? (
          <>
            {/* Горизонтальный размер */}
            <Line
              points={[
                x1 - position.x,
                y1 - position.y,
                x1 - position.x,
                0,
              ]}
              stroke="#000000"
              strokeWidth={1}
              dash={[4, 4]}
              listening={false}
            />
            <Line
              points={[
                x2 - position.x,
                y2 - position.y,
                x2 - position.x,
                0,
              ]}
              stroke="#000000"
              strokeWidth={1}
              dash={[4, 4]}
              listening={false}
            />
            <Arrow
              points={[
                x1 - position.x,
                0,
                x2 - position.x,
                0,
              ]}
              stroke="#000000"
              strokeWidth={2}
              pointerLength={10}
              pointerWidth={10}
              fill="#000000"
              pointerAtBeginning
              pointerAtEnding
              hitStrokeWidth={10}
            />

            {/* Текст (EditableText) */}
            {(() => {
              const fs = (dim.textFontSize || 16) * (textZoom || 1);
              const tempText = new Konva.Text({ text: getDisplayText(), fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
              const textW = tempText.width();
              const textH = tempText.height();
              const margin = 10;
              const centerX = (x1 + x2) / 2 - position.x;
              // Базовые координаты без учета textPosition
              const baseXNoOffset = centerX;
              const baseYNoOffset = -(textH / 2 + margin);
              const baseX = baseXNoOffset + (textPosition?.x || 0);
              const baseY = baseYNoOffset + (textPosition?.y || 0);
              const labelW = labelWidthAlong(textW);
              const spanL = Math.min(x1, x2) - position.x;
              const spanR = Math.max(x1, x2) - position.x;
              const labelL = baseX - labelW / 2;
              const labelR = baseX + labelW / 2;
            return (
              <>
                {labelL < spanL - 0.5 && (
                  <Line points={[labelL - 2, 0, spanL, 0]} stroke="#000000" strokeWidth={2} listening={false} />
                )}
                {labelR > spanR + 0.5 && (
                  <Line points={[spanR, 0, labelR + 2, 0]} stroke="#000000" strokeWidth={2} listening={false} />
                )}
                <EditableText
                  x={baseX}
                  y={baseY}
                  text={getDisplayText()}
                  fontSize={fs}
                  fontFamily={textFontFamily}
                  fontStyle={textFontStyle}
                  fill={textColor}
                  onChangeLive={false}
                  rotation={0}
                  offsetX={textW / 2}
                  offsetY={textH / 2}
                  isEditable={true}
                  onChange={(newValue) => {
                    if (!lockGeometry) {
                      handleDimensionChange(newValue, index);
                    }
                  }}
                  draggable={isShiftPressed}
                  dragBoundFunc={function(pos) {
                    // Для горизонтального размера текст может двигаться только по X (параллельно размерной линии)
                    const fixedY = this._dragFixedY !== undefined ? this._dragFixedY : baseY;
                    return { x: pos.x, y: fixedY };
                  }}
                  onDragStart={(evt) => {
                    // Блокируем перетаскивание при использовании средней кнопки мыши (панорамирование)
                    if (evt.evt && evt.evt.button === 1) {
                      evt.target.stopDrag();
                      return;
                    }
                    evt.cancelBubble = true;
                    const textNode = evt.target;
                    // Сохраняем начальную позицию и начальное значение textPosition
                    textNode.dragStartPos = { ...textNode.position() };
                    textNode.dragStartTextPosition = { x: textPosition?.x || 0, y: textPosition?.y || 0 };
                    // Сохраняем фиксированное значение Y для dragBoundFunc
                    textNode._dragFixedY = textNode.dragStartPos.y;
                  }}
                  onDragMove={(evt) => {
                    evt.cancelBubble = true;
                    const textNode = evt.target;
                    const newPos = textNode.position();
                    
                    // ПРИНУДИТЕЛЬНО фиксируем Y координату
                    if (newPos.y !== textNode._dragFixedY) {
                      textNode.y(textNode._dragFixedY);
                    }
                    
                    // Вычисляем смещение от начала перетаскивания
                    const deltaX = newPos.x - textNode.dragStartPos.x;
                    let updatedDim = { ...dim };
                    // Обновляем только X компонент textPosition (параллельно горизонтальной линии)
                    updatedDim.textPosition = {
                      x: textNode.dragStartTextPosition.x + deltaX,
                      y: textNode.dragStartTextPosition.y,
                    };
                    const newDims = [...dimensionLines];
                    newDims[index] = updatedDim;
                    setDimensionLines(newDims);
                    updateAllDimensionLines();
                  }}
                  onDragEnd={(evt) => {
                    evt.cancelBubble = true;
                    saveState();
                  }}
                />
                {lowerOverrideEnabled && (() => {
                  const mainX = baseX;
                  const mainY = baseY;
                  const marginUnder = (typeof dim.horizontalLowerMargin === 'number') ? dim.horizontalLowerMargin : 10; // отдельный отступ для горизонтали
                  const offset = textH + marginUnder;
                  // Горизонталь: нижний текст строго под основным по экрану
                  const lowerX = mainX;
                  const lowerY = mainY + offset;
                  const lowerTextContent = (typeof lowerOverrideText === 'string' && lowerOverrideText.length > 0) ? lowerOverrideText : ' ';
                  const tempLower = new Konva.Text({ text: lowerTextContent, fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
                  const lowerW = tempLower.width();
                  return (
                    <EditableText
                      x={lowerX}
                      y={lowerY}
                      text={lowerTextContent}
                      fontSize={fs}
                      fontFamily={textFontFamily}
                      fontStyle={textFontStyle}
                      align="center"
                      lineHeight={1.2}
                      fill={textColor}
                      rotation={0}
                      offsetX={lowerW / 2}
                      offsetY={0}
                      isEditable={true}
                      onChange={(val) => {
                        const newDims = [...dimensionLines];
                        newDims[index] = { ...dim, lowerOverrideText: val };
                        setDimensionLines(newDims);
                      }}
                      onChangeLive={false}
                    />
                  );
                })()}
              </>
            );
            })()}
          </>
        ) : (
          <>
            {/* Вертикальный размер */}
            <Line
              points={[
                x1 - position.x,
                y1 - position.y,
                0,
                y1 - position.y,
              ]}
              stroke="#000000"
              strokeWidth={1}
              dash={[4, 4]}
              listening={false}
            />
            <Line
              points={[
                x2 - position.x,
                y2 - position.y,
                0,
                y2 - position.y,
              ]}
              stroke="#000000"
              strokeWidth={1}
              dash={[4, 4]}
              listening={false}
            />
            <Arrow
              points={[
                0,
                y1 - position.y,
                0,
                y2 - position.y,
              ]}
              stroke="#000000"
              strokeWidth={2}
              pointerLength={10}
              pointerWidth={10}
              fill="#000000"
              pointerAtBeginning
              pointerAtEnding
              hitStrokeWidth={10}
            />

            {/* Текст (EditableText), повернутый на -90 */}
            {(() => {
              const fs = (dim.textFontSize || 16) * (textZoom || 1);
              const tempText = new Konva.Text({ text: getDisplayText(), fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
              const textW = tempText.width();
              const textH = tempText.height();
              const margin = 10;
              const centerY = (y1 + y2) / 2 - position.y;
              // Базовые координаты без учета textPosition
              const baseXNoOffset = -(textH / 2 + margin);
              const baseYNoOffset = centerY;
              const baseX = baseXNoOffset + (textPosition?.x || 0);
              const baseY = baseYNoOffset + (textPosition?.y || 0);
              const labelW = labelWidthAlong(textW);
              const spanT = Math.min(y1, y2) - position.y;
              const spanB = Math.max(y1, y2) - position.y;
              const labelT = baseY - labelW / 2;
              const labelB = baseY + labelW / 2;
              
            return (
              <>
                {labelT < spanT - 0.5 && (
                  <Line points={[0, labelT - 2, 0, spanT]} stroke="#000000" strokeWidth={2} listening={false} />
                )}
                {labelB > spanB + 0.5 && (
                  <Line points={[0, spanB, 0, labelB + 2]} stroke="#000000" strokeWidth={2} listening={false} />
                )}
                <EditableText
                  x={baseX}
                  y={baseY}
                  text={getDisplayText()}
                  fontSize={fs}
                  fontFamily={textFontFamily}
                  fontStyle={textFontStyle}
                  fill={textColor}
                  onChangeLive={false}
                  rotation={-90}
                  offsetX={textW / 2}
                  offsetY={textH / 2}
                  isEditable={true}
                  onChange={(newValue) => {
                    if (!lockGeometry) {
                      handleDimensionChange(newValue, index);
                    }
                  }}
                  draggable={isShiftPressed}
                  dragBoundFunc={function(pos) {
                    // Для вертикального размера текст может двигаться только по Y (параллельно размерной линии)
                    const fixedX = this._dragFixedX !== undefined ? this._dragFixedX : baseX;
                    return { x: fixedX, y: pos.y };
                  }}
                  onDragStart={(evt) => {
                    // Блокируем перетаскивание при использовании средней кнопки мыши (панорамирование)
                    if (evt.evt && evt.evt.button === 1) {
                      evt.target.stopDrag();
                      return;
                    }
                    evt.cancelBubble = true;
                    const textNode = evt.target;
                    // Сохраняем начальную позицию и начальное значение textPosition
                    textNode.dragStartPos = { ...textNode.position() };
                    textNode.dragStartTextPosition = { x: textPosition?.x || 0, y: textPosition?.y || 0 };
                    // Сохраняем фиксированное значение X для dragBoundFunc
                    textNode._dragFixedX = textNode.dragStartPos.x;
                  }}
                  onDragMove={(evt) => {
                    evt.cancelBubble = true;
                    const textNode = evt.target;
                    const newPos = textNode.position();
                    
                    // ПРИНУДИТЕЛЬНО фиксируем X координату
                    if (newPos.x !== textNode._dragFixedX) {
                      textNode.x(textNode._dragFixedX);
                    }
                    
                    // Вычисляем смещение от начала перетаскивания
                    const deltaY = newPos.y - textNode.dragStartPos.y;
                    let updatedDim = { ...dim };
                    // Обновляем только Y компонент textPosition (параллельно вертикальной линии)
                    updatedDim.textPosition = {
                      x: textNode.dragStartTextPosition.x,
                      y: textNode.dragStartTextPosition.y + deltaY,
                    };
                    const newDims = [...dimensionLines];
                    newDims[index] = updatedDim;
                    setDimensionLines(newDims);
                    updateAllDimensionLines();
                  }}
                  onDragEnd={(evt) => {
                    evt.cancelBubble = true;
                    saveState();
                  }}
                />
                {lowerOverrideEnabled && (() => {
                  const mainX = baseX;
                  const mainY = baseY;
                  const marginUnder = (typeof dim.verticalLowerMargin === 'number') ? dim.verticalLowerMargin : 15;
                  // Вертикаль: нижний текст справа от основного по экрану
                  const lowerX = mainX + (textH / 2) + marginUnder;
                  const lowerY = mainY;
                  const lowerTextContent = (typeof lowerOverrideText === 'string' && lowerOverrideText.length > 0) ? lowerOverrideText : ' ';
                  const tempLower = new Konva.Text({ text: lowerTextContent, fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
                  const lowerW = tempLower.width();
                  return (
                    <EditableText
                      x={lowerX}
                      y={lowerY}
                      text={lowerTextContent}
                      fontSize={fs}
                      fontFamily={textFontFamily}
                      fontStyle={textFontStyle}
                      align="center"
                      lineHeight={1.2}
                      fill={textColor}
                      rotation={-90}
                      offsetX={lowerW / 2}
                      offsetY={0}
                  isEditable={true}
                      onChange={(val) => {
                        const newDims = [...dimensionLines];
                        newDims[index] = { ...dim, lowerOverrideText: val };
                        setDimensionLines(newDims);
                      }}
                      onChangeLive={false}
                    />
                  );
                })()}
              </>
            );
            })()}
          </>
        )
      ) : (
        <>
          {(() => {
            const rad = (angleDeg * Math.PI) / 180;
            const ux = Math.cos(rad);
            const uy = Math.sin(rad);
            // Проекции концов на размерную линию, заданную точкой position и направлением u
            const tx1 = (x1 - position.x) * ux + (y1 - position.y) * uy;
            const tx2 = (x2 - position.x) * ux + (y2 - position.y) * uy;
            // Мировые проекции для пунктира
            const p1px = position.x + tx1 * ux;
            const p1py = position.y + tx1 * uy;
            const p2px = position.x + tx2 * ux;
            const p2py = position.y + tx2 * uy;

            return (
              <>
                {/* Пунктир к проекциям */}
                <Line
                  points={[x1 - position.x, y1 - position.y, p1px - position.x, p1py - position.y]}
                  stroke="#000000"
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
                <Line
                  points={[x2 - position.x, y2 - position.y, p2px - position.x, p2py - position.y]}
                  stroke="#000000"
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
                {/* Стрелка от проекции к проекции в координатах группы */}
                <Arrow
                  points={[tx1 * ux, tx1 * uy, tx2 * ux, tx2 * uy]}
                  stroke="#000000"
                  strokeWidth={2}
                  pointerLength={10}
                  pointerWidth={10}
                  fill="#000000"
                  pointerAtBeginning
                  pointerAtEnding
                  hitStrokeWidth={10}
                />
              </>
            );
          })()}

          {(() => {
            const rad = (angleDeg * Math.PI) / 180;
            const ux = Math.cos(rad);
            const uy = Math.sin(rad);
            const nx = -uy;
            const ny = ux;
            const tx1 = (x1 - position.x) * ux + (y1 - position.y) * uy;
            const tx2 = (x2 - position.x) * ux + (y2 - position.y) * uy;
            const tMid = (tx1 + tx2) / 2;
            const baseX = tMid * ux;
            const baseY = tMid * uy;
            // Измеряем размеры текста и корректно учитываем поворот текста: проецируем нормаль
            // в локальные оси текста, чтобы зазор оставался постоянным при масштабировании.
            const tempText = new Konva.Text({ text: getDisplayText(), fontSize: (dim.textFontSize || 16) * (textZoom || 1), fontFamily: textFontFamily, fontStyle: textFontStyle });
            const textW = tempText.width();
            const textH = tempText.height();
            // Отступ основного текста оставляем единым
            const marginMain = 10;
            // Держим текст "ровным" относительно экрана, а не перевёрнутым
            const upright = angleDeg <= 90 ? angleDeg : angleDeg - 180;
            // Перпендикуляр к стрелке и дополнительно нормализуем в диапазон [-90, 90],
            // чтобы текст не оказывался вверх ногами
            const textRotationRaw = -upright - 90;
            let textRotation = textRotationRaw;
            if (textRotation > 180) textRotation -= 360;
            if (textRotation <= -180) textRotation += 360;
            if (textRotation > 90) textRotation -= 180;
            if (textRotation < -90) textRotation += 180;
            // Проекция половинных размеров на нормаль с учётом поворота текста: nLocal = R(-theta) * n
            const theta = (textRotation * Math.PI) / 180;
            const cosT = Math.cos(theta);
            const sinT = Math.sin(theta);
            const nLocalX = nx * cosT + ny * sinT;      // x' = nx*cos - ny*sin, но тут R(-theta): cos, +sin
            const nLocalY = -nx * sinT + ny * cosT;     // y' = nx*sin + ny*cos, с учётом знаков для R(-theta)
            const projectedHalfSize = Math.abs(nLocalX) * (textW / 2) + Math.abs(nLocalY) * (textH / 2);
            // Для диагонали: основной текст всегда над стрелкой, нижний — под стрелкой
            // Это означает, что смещение вдоль нормали должно подниматься против направления нормали,
            // если нормаль смотрит вниз по экрану, и опускаться, если нормаль смотрит вверх.
            const signNy = Math.sign(ny) || 1; // ny > 0 — нормаль вниз по экрану
            const nShift = -signNy * (projectedHalfSize + marginMain);
            // Базовые координаты без учета textPosition
            const baseXNoOffset = baseX + nx * nShift;
            const baseYNoOffset = baseY + ny * nShift;
            const textX = baseXNoOffset + (textPosition?.x || 0);
            const textY = baseYNoOffset + (textPosition?.y || 0);
            // Центрируем текст относительно середины стрелки
            return (
              <EditableText
                x={textX}
                y={textY}
                text={getDisplayText()}
                fontSize={(dim.textFontSize || 16) * (textZoom || 1)}
                fontFamily={textFontFamily}
                fontStyle={textFontStyle}
                fill={textColor}
                onChangeLive={false}
                rotation={textRotation}
                offsetX={textW / 2}
                offsetY={textH / 2}
                isEditable={true}
                onChange={(newValue) => {
                  if (!lockGeometry) {
                    handleDimensionChange(newValue, index);
                  }
                }}
                draggable={isShiftPressed}
                dragBoundFunc={function(pos) {
                  // Для диагонального размера текст может двигаться только вдоль размерной линии (параллельно)
                  // Используем сохраненную базовую позицию
                  const basePos = this._dragBasePos || { x: textX, y: textY };
                  // Проецируем смещение от базовой позиции на направление размерной линии (ux, uy)
                  const currentOffsetX = pos.x - basePos.x;
                  const currentOffsetY = pos.y - basePos.y;
                  const projectionOnLine = currentOffsetX * ux + currentOffsetY * uy;
                  return {
                    x: basePos.x + projectionOnLine * ux,
                    y: basePos.y + projectionOnLine * uy
                  };
                }}
                onDragStart={(evt) => {
                  // Блокируем перетаскивание при использовании средней кнопки мыши (панорамирование)
                  if (evt.evt && evt.evt.button === 1) {
                    evt.target.stopDrag();
                    return;
                  }
                  evt.cancelBubble = true;
                  const textNode = evt.target;
                  // Сохраняем начальную позицию и начальное значение textPosition
                  textNode.dragStartPos = { ...textNode.position() };
                  textNode.dragStartTextPosition = { x: textPosition?.x || 0, y: textPosition?.y || 0 };
                  // Сохраняем базовую позицию для dragBoundFunc
                  textNode._dragBasePos = { ...textNode.dragStartPos };
                }}
                onDragMove={(evt) => {
                  evt.cancelBubble = true;
                  const textNode = evt.target;
                  const newPos = textNode.position();
                  
                  // Вычисляем смещение от начала перетаскивания
                  const deltaX = newPos.x - textNode.dragStartPos.x;
                  const deltaY = newPos.y - textNode.dragStartPos.y;
                  // Проецируем дельту на направление размерной линии
                  const projectedDelta = deltaX * ux + deltaY * uy;
                  
                  // ПРИНУДИТЕЛЬНО устанавливаем позицию вдоль линии
                  const correctedX = textNode.dragStartPos.x + projectedDelta * ux;
                  const correctedY = textNode.dragStartPos.y + projectedDelta * uy;
                  textNode.position({ x: correctedX, y: correctedY });
                  
                  let updatedDim = { ...dim };
                  // Обновляем textPosition только вдоль направления размерной линии
                  updatedDim.textPosition = {
                    x: textNode.dragStartTextPosition.x + projectedDelta * ux,
                    y: textNode.dragStartTextPosition.y + projectedDelta * uy,
                  };
                  const newDims = [...dimensionLines];
                  newDims[index] = updatedDim;
                  setDimensionLines(newDims);
                  updateAllDimensionLines();
                }}
                onDragEnd={(evt) => {
                  evt.cancelBubble = true;
                  saveState();
                }}
              />
            );
          })()}

          {(() => {
            // Линия-указка от стрелки к тексту для повернутых размеров (фиксированный вынос)
            const rad = (angleDeg * Math.PI) / 180;
            const ux = Math.cos(rad);
            const uy = Math.sin(rad);
            // Проекции концов на ось размера
            const tx1 = (x1 - position.x) * ux + (y1 - position.y) * uy;
            const tx2 = (x2 - position.x) * ux + (y2 - position.y) * uy;
            const tMid = (tx1 + tx2) / 2;
            // Позиция текста вдоль оси размера с учётом textPosition в координатах группы
            const tText = tMid + (textPosition?.x || 0) * ux + (textPosition?.y || 0) * uy;
            const minT = Math.min(tx1, tx2);
            const maxT = Math.max(tx1, tx2);
            if (tText < minT || tText > maxT) {
              const tAnchor = tText < minT ? minT : maxT;
              // Фиксированный вынос за текст вдоль оси, чтобы линия не "обгоняла" текст
              const dir = tText >= tAnchor ? 1 : -1;
              const fs = (dim.textFontSize || 16) * (textZoom || 1);
              const label = new Konva.Text({ text: getDisplayText(), fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
              const extra = labelWidthAlong(label.width()) / 2 + 2;
              const tEnd = tText + dir * extra;
              return (
                <Line
                  points={[tAnchor * ux, tAnchor * uy, tEnd * ux, tEnd * uy]}
                  stroke="#000000"
                  strokeWidth={1}
                  hitStrokeWidth={10}
                />
              );
            }
            return null;
          })()}

          {lowerOverrideEnabled && (() => {
            // Поверх стрелок/пунктира: нижний текст под основным вдоль нормали
            const rad = (angleDeg * Math.PI) / 180;
            const ux = Math.cos(rad);
            const uy = Math.sin(rad);
            const nx = -uy;
            const ny = ux;
            const tx1 = (x1 - position.x) * ux + (y1 - position.y) * uy;
            const tx2 = (x2 - position.x) * ux + (y2 - position.y) * uy;
            const tMid = (tx1 + tx2) / 2;
            const baseX = tMid * ux;
            const baseY = tMid * uy;
            const fs = (dim.textFontSize || 16) * (textZoom || 1);
            const tempText = new Konva.Text({ text: getDisplayText(), fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
            const textW = tempText.width();
            const textH = tempText.height();
            const margin = 10;
            const upright = angleDeg <= 90 ? angleDeg : angleDeg - 180;
            const textRotationRaw = -upright - 90;
            let textRotation = textRotationRaw;
            if (textRotation > 180) textRotation -= 360;
            if (textRotation <= -180) textRotation += 360;
            if (textRotation > 90) textRotation -= 180;
            if (textRotation < -90) textRotation += 180;
            const theta = (textRotation * Math.PI) / 180;
            const cosT = Math.cos(theta);
            const sinT = Math.sin(theta);
            const nLocalX = nx * cosT + ny * sinT;
            const nLocalY = -nx * sinT + ny * cosT;
            const projectedHalfSize = Math.abs(nLocalX) * (textW / 2) + Math.abs(nLocalY) * (textH / 2);
            const nShift = -(projectedHalfSize + margin);
            const mainX = baseX + nx * nShift + (textPosition?.x || 0);
            const mainY = baseY + ny * nShift + (textPosition?.y || 0);
            // Нижний текст: отдельные отступы под правый/левый 45°
            const isRightLower = ux >= 0;
            const marginUnderRight = typeof dim.diagLowerMarginRight === 'number' ? dim.diagLowerMarginRight : 10;
            const marginUnderLeft  = typeof dim.diagLowerMarginLeft === 'number' ? dim.diagLowerMarginLeft : -5;
            const marginUnder = isRightLower ? marginUnderRight : marginUnderLeft;
            const offset = textH + marginUnder;
            const lowerX = mainX + nx * offset;
            const lowerY = mainY + ny * offset;
            const lowerTextContent = (typeof lowerOverrideText === 'string' && lowerOverrideText.length > 0) ? lowerOverrideText : ' ';
            const tempLower = new Konva.Text({ text: lowerTextContent, fontSize: fs, fontFamily: textFontFamily, fontStyle: textFontStyle });
            const lowerW = tempLower.width();
            return (
              <EditableText
                x={lowerX}
                y={lowerY}
                text={lowerTextContent}
                fontSize={fs}
                fontFamily={textFontFamily}
                fontStyle={textFontStyle}
                align="center"
                lineHeight={1.2}
                fill={textColor}
                rotation={textRotation}
                offsetX={lowerW / 2}
                offsetY={0}
                isEditable={true}
                onChange={(val) => {
                  const newDims = [...dimensionLines];
                  newDims[index] = { ...dim, lowerOverrideText: val };
                  setDimensionLines(newDims);
                }}
                onChangeLive={false}
              />
            );
          })()}
        </>
      )}

      {/* Продолжение размерной линии под длинной надписью рисуется рядом со стрелкой, по ширине текста. */}
    </Group>
  );
}

export default DimensionLineComponent;
